/**
 * Equalizes the height of matching spec rows across all cards in a
 * Product Spec Carousel section, so that row N of every card lines up with
 * row N of the others — even when one value wraps onto more lines.
 *
 * Pure measure-then-write: all reads happen first, then all writes, so there
 * is no layout thrashing. Runs once on load, on resize (debounced via
 * requestAnimationFrame), and whenever the section re-renders in the theme
 * editor.
 */
class ProductSpecCarousel extends HTMLElement {
  #rafId = null;
  #resizeObserver = null;

  connectedCallback() {
    this.#scheduleEqualize();

    // Observe the track, NOT `this`: the wrapper is `display: contents`, so it
    // generates no box and a ResizeObserver on it would never fire. The track
    // is a real element whose width changes on every viewport/layout resize.
    const track = this.querySelector('[data-slider-track]');
    this.#resizeObserver = new ResizeObserver(() => this.#scheduleEqualize());
    this.#resizeObserver.observe(track || this);

    // Re-run once every image has loaded — images change card/row heights.
    this.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => this.#scheduleEqualize(), { once: true });
      }
    });
  }

  disconnectedCallback() {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#resizeObserver?.disconnect();
  }

  #scheduleEqualize() {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#equalizeRows();
    });
  }

  #equalizeRows() {
    const cards = this.querySelectorAll('.psc-card');
    if (cards.length < 2) return;

    // Group rows by their index within each card.
    const rowsByIndex = [];
    cards.forEach((card) => {
      card.querySelectorAll('.psc-card__spec-row').forEach((row, index) => {
        (rowsByIndex[index] ||= []).push(row);
      });
    });

    // RESET phase: drop every override first, so a widened viewport (text that
    // no longer wraps) is measured at its smaller natural height, not the old
    // inflated one. Done in its own pass, before any read.
    rowsByIndex.forEach((rows) => {
      rows.forEach((row) => {
        row.style.minHeight = '';
      });
    });

    // READ phase: measure the tallest natural height per row index.
    const maxHeights = rowsByIndex.map((rows) =>
      rows.reduce((max, row) => Math.max(max, row.getBoundingClientRect().height), 0)
    );

    // WRITE phase: apply the tallest height to every row in that group.
    rowsByIndex.forEach((rows, index) => {
      const height = `${maxHeights[index]}px`;
      rows.forEach((row) => {
        row.style.minHeight = height;
      });
    });
  }
}

customElements.define('product-spec-carousel', ProductSpecCarousel);
